import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ProductResultCard } from "./ProductResultCard";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Variant {
  id: string;
  title: string;
  price: string;
  currency: string;
  availableForSale: boolean;
  selectedOptions?: Array<{ name: string; value: string }>;
  image?: string | null;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  price: string;
  currency: string;
  image: string | null;
  variantId: string;
  availableForSale: boolean;
  variants?: Variant[];
  options?: Array<{ name: string; values: string[] }>;
  selectedVariantIndex?: number;
}

interface ProductResultsProps {
  products: Product[];
}

export function ProductResults({ products }: ProductResultsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      // In RTL, scrollLeft starts at 0 and goes negative as you scroll left
      const currentScroll = Math.abs(container.scrollLeft);
      
      // canScrollLeft: can we scroll more to the LEFT (to see more products)
      // In RTL, more products are revealed by scrolling left (scrollLeft becomes more negative)
      setCanScrollLeft(currentScroll < maxScroll - 1);
      
      // canScrollRight: can we scroll back to the RIGHT (to see earlier products)
      // In RTL, we can scroll right if we've already scrolled left
      setCanScrollRight(currentScroll > 1);
    }
  };

  useEffect(() => {
    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      // In RTL: scrolling left = negative delta, scrolling right = positive delta
      const delta = direction === 'left' ? -scrollAmount : scrollAmount;
      container.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  if (products.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-3 p-3 bg-background/50 rounded-lg text-center"
      >
        <p className="text-sm text-muted-foreground">
          לא נמצאו תכשיטים מתאימים. נסה לשנות את הקריטריונים 🔍
        </p>
      </motion.div>
    );
  }

  const showScrollButtons = products.length > 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-3 relative"
    >
      {/* Scroll Left Button - appears on LEFT side, arrow points LEFT */}
      {showScrollButtons && canScrollLeft && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full shadow-lg bg-background/90 hover:bg-background border"
          onClick={() => scroll('left')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}

      {/* Scroll Right Button - appears on RIGHT side, arrow points RIGHT */}
      {showScrollButtons && canScrollRight && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full shadow-lg bg-background/90 hover:bg-background border"
          onClick={() => scroll('right')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      <div 
        ref={scrollContainerRef}
        className="flex gap-2 pb-2 overflow-x-auto scrollbar-hide px-1"
        dir="rtl"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product, index) => (
          <div key={product.id} className="w-[140px] flex-shrink-0">
            <ProductResultCard product={product} index={index} />
          </div>
        ))}
      </div>
    </motion.div>
  );
}
